<script lang="ts" setup>
import { onClickOutside } from '@vueuse/core';
import { ref, computed, useTemplateRef } from 'vue';
import { IconName } from '../UiIcon.vue';

export interface MultiSelectOption {
  label: string;
  value: string;
}

const props = defineProps<{
  modelValue: string[];
  options: MultiSelectOption[];
  placeholder?: string;
  placeholderIcon?: IconName;
  disabled?: boolean;
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', p: string[]): void;
  (e: 'open'): void;
  (e: 'close'): void;
}>();

const uiMultiSelect = useTemplateRef<HTMLDivElement>('uiMultiSelect');
const showOptions = ref(false);
const searchQuery = ref('');
const searchInput = useTemplateRef<HTMLInputElement>('searchInput');
const dropdownMaxHeight = ref(400);

async function toggleOptionsVisibility() {
  if (props.disabled) {
    return;
  }
  showOptions.value = !showOptions.value;
  if (showOptions.value) {
    await nextTick();
    // Clamp computed only on open — does not react to resize or layout shift.
    const rect = uiMultiSelect.value?.getBoundingClientRect();
    if (rect) {
      dropdownMaxHeight.value = Math.min(
        400,
        window.innerHeight - rect.bottom - 16,
      );
    }
    searchInput.value?.focus();
    emit('open');

    return;
  }
  emit('close');
}

onClickOutside(uiMultiSelect, () => {
  showOptions.value = false;
  emit('close');
});

function checkOption(val: string) {
  if (!props.modelValue.includes(val)) {
    return emit('update:modelValue', props.modelValue.concat(val));
  }
  const newValues = props.modelValue.slice();
  const valIndex = newValues.findIndex((v) => v === val);
  newValues.splice(valIndex, 1);
  emit('update:modelValue', newValues);
}

function isChecked(val: string) {
  return props.modelValue.includes(val);
}

function getLabel(value: string) {
  return props.options.find((option) => option.value === value)?.label;
}

const filteredOptions = computed(() => {
  return props.options.filter((option) =>
    option.label.toLowerCase().includes(searchQuery.value.toLowerCase()),
  );
});
</script>

<template>
  <div ref="uiMultiSelect" class="ui-multi-select">
    <div
      class="selection"
      :class="{ 'is-disabled': props.disabled }"
      @click="toggleOptionsVisibility"
    >
      <div class="selectedOptions">
        <template v-if="!showOptions">
          <span
            v-for="option in props.modelValue"
            :key="option"
            class="selectedOption"
          >
            {{ getLabel(option) }}
          </span>
        </template>

        <div v-if="!props.modelValue.length" class="placeholder">
          <UiIcon
            v-if="props.placeholderIcon"
            class="icon-size-sm placeholder-icon"
            :name="props.placeholderIcon"
          />
          <span v-if="props.placeholder && !showOptions">{{
            props.placeholder
          }}</span>
        </div>
      </div>
      <input
        v-if="showOptions"
        ref="searchInput"
        v-model="searchQuery"
        type="text"
        :placeholder="$t('random.search')"
        class="input-search"
        @click.stop
      />
      <UiIcon v-if="showOptions" name="ChevronUp" :size="20" />
      <UiIcon v-else name="ChevronDown" :size="20" />
    </div>

    <transition>
      <div
        v-if="showOptions"
        class="options"
        :style="{ maxHeight: `${dropdownMaxHeight}px` }"
      >
        <div
          v-for="option in filteredOptions"
          :key="option.value"
          class="option"
          @click="checkOption(option.value)"
        >
          <UiCheckbox
            :model-value="isChecked(option.value)"
            :name="`${option.value}`"
          />
          {{ option.label }}
        </div>
      </div>
    </transition>
  </div>
</template>

<style lang="scss" scoped>
.ui-multi-select {
  position: relative;
  width: 100%;
}

.selection {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border: 1px solid var(--color-gray-300);
  box-shadow: var(--shadow-xs);
  background: #ffffff;
  min-height: px(45);
  padding: 0 var(--space-10) 0 px(0);
  user-select: none;
  cursor: pointer;
  border-radius: var(--border-radius-4);
  // The search field inside is borderless; the ring goes round the box.
  @include focus-ring-within;
}

.selectedOptions {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  padding: 0 0 0 px(14);
}
svg {
  color: var(--color-gray-500);
}

.selectedOption + .selectedOption {
  margin-left: var(--space-4);
}

.selectedOption:not(:last-child):after {
  content: ',';
}

.options {
  position: absolute;
  width: 100%;
  z-index: 10;
  border: 1px solid var(--color-gray-300);
  box-shadow: var(--shadow-xs);
  background: #ffffff;
  padding-top: var(--space-8);
  overflow-y: auto;
  border-radius: var(--border-radius-4);
  margin-top: var(--space-4);
}

.option {
  display: flex;
  gap: var(--space-12);
  padding: var(--space-8) px(14);

  &:hover {
    background: var(--color-gray-50);
    box-shadow: inset 0 0 4px var(--color-gray-100);
  }
}

.placeholder {
  display: flex;
  align-items: center;
  gap: 5px;
}

.placeholder-icon {
  color: var(--color-gray-400);
}

label.checkbox {
  margin-bottom: 0 !important;
}

.v-enter-active,
.v-leave-active {
  transition: all var(--duration-slow) var(--ease-standard);
  margin-top: 0;
}

.v-enter-from,
.v-leave-to {
  opacity: 0;
  margin-top: -1rem;
}

.is-disabled {
  opacity: 0.5;
  .select-label {
    cursor: default;
  }
}

input.input-search {
  width: 100%;
  flex-grow: 1;
  border: none;
  outline: none;
}
</style>
